import { formatPence, formatPrice, shortDate } from './money';
import { meter } from './usageRules';

/**
 * Spend protection (pricing, Oct 2026): a monthly limit on what we charge a
 * card automatically (auto top-ups and receptionist packs), a pause when
 * spending jumps unusually fast, and settlements held for a quick check.
 *
 * usage.balance.autoSpend { capPence, spentPence, reachedAt, alertLevel,
 *   topupsToday, maxTopupsPerDay }, balance.autoPausedAt / autoPausedReason,
 * balance.settlement.heldForReviewAt / heldPence.
 * PUT /billing/auto-spend-cap { capPence } (£10–£500) · POST /billing/auto-resume.
 */

export const CAP_MIN_PENCE = 1000;
export const CAP_MAX_PENCE = 50000;
// Sensible steps between £10 and £500.
export const CAP_STEPS = [1000, 2000, 3000, 5000, 7500, 10000, 15000, 20000, 30000, 40000, 50000];

const num = (v) => (v == null || v === '' || !Number.isFinite(Number(v)) ? null : Number(v));

/** Keep a limit inside £10–£500, in whole pounds. */
export function clampCap(pence) {
    const n = num(pence);
    if (n == null) return CAP_STEPS[3];
    return Math.min(CAP_MAX_PENCE, Math.max(CAP_MIN_PENCE, Math.round(n / 100) * 100));
}
/** The next step up / down from where the limit is now (off-step values snap). */
export function stepCap(pence, dir) {
    const p = clampCap(pence);
    if (dir > 0) return CAP_STEPS.find((s) => s > p) || CAP_MAX_PENCE;
    return [...CAP_STEPS].reverse().find((s) => s < p) || CAP_MIN_PENCE;
}

/** Everything the balance section and the summary need, or null without a balance. */
export function autoSpendState(usage) {
    const bal = usage && usage.balance;
    if (!bal) return null;
    const a = bal.autoSpend || null;
    const settle = bal.settlement || {};
    const cap = a ? num(a.capPence) : null;
    const spent = a ? num(a.spentPence) || 0 : 0;
    return {
        hasCap: cap != null,
        capPence: cap,
        spentPence: spent,
        meter: cap != null ? meter(spent, cap) : null,
        alertLevel: a ? num(a.alertLevel) || 0 : 0,
        reached: !!(a && a.reachedAt),
        reachedAt: a ? a.reachedAt || null : null,
        topupsToday: a ? num(a.topupsToday) : null,
        maxTopupsPerDay: a ? num(a.maxTopupsPerDay) : null,
        paused: !!bal.autoPausedAt,
        pausedAt: bal.autoPausedAt || null,
        pausedReason: bal.autoPausedReason || null,
        held: !!settle.heldForReviewAt,
        heldPence: num(settle.heldPence),
    };
}

/**
 * Who may change the limit or resume: the owner (the business pot) or a
 * self-employed stylist who pays their own (not when the salon covers them).
 * Staff never. The server enforces this too (403).
 */
export function canManageSpend(usage, { isOwner } = {}) {
    if (!usage || !usage.balance) return false;
    if (usage.viewer === 'staff') return false;
    if (usage.viewer === 'self-employed' || (usage.plan && usage.plan.type === 'self-employed-in-salon')) return !usage.balanceCoveredBySalon;
    return !!isOwner;
}

export const spendCopy = {
    capLine: (s) => `Automatic charges this month: ${formatPrice(s.spentPence)} of ${formatPrice(s.capPence)}`,
    capSr: (s) => `${formatPence(s.spentPence)} of ${formatPrice(s.capPence)} charged automatically this month`,
    promise: 'We’ll never charge your card automatically beyond this in a month.',
    paused: 'We paused automatic top-ups because spending jumped unusually fast. Nothing else has stopped, and reminders still send.',
    resumeConfirm: (s) => `Resume automatic top-ups? We’ll start charging your card automatically again${s && s.capPence != null ? `, up to your ${formatPrice(s.capPence)} limit a month` : ''}.`,
    reached: (s) => `You’ve reached this month’s automatic limit (${formatPrice(s.capPence)}). Top up by hand or raise the limit.`,
    held: (s) => (s.heldPence != null
        ? `Your ${formatPrice(s.heldPence)} of reminder texts is with our team for a quick check before we charge it. Reminders keep sending.`
        : 'Your reminder texts are with our team for a quick check before we charge them. Reminders keep sending.'),
    nearly: (s) => `You’ve used most of this month’s automatic limit (${formatPrice(s.capPence)}).`,
};

/** A failed save of the limit or resume, in plain words. */
export function spendErrorText(e) {
    const status = e && e.status;
    if (status === 400) return `Choose a limit between ${formatPrice(CAP_MIN_PENCE)} and ${formatPrice(CAP_MAX_PENCE)}.`;
    if (status === 403) return 'Only whoever pays for this balance can change it.';
    return 'That didn’t save just now. Try again in a moment.';
}

/** "since 3 Oct", for the paused card. */
export const pausedSince = (s, now) => (s && s.pausedAt ? shortDate(s.pausedAt, now) : '');
